import { formatCurrencyValue } from '@/modules/common/lib/formatters';
import { ApiLearningBudgetTransactionsDigestResponse } from '@/types/learning-budget';
import { PeopleAltOutlined } from '@mui/icons-material';
import { Skeleton, Typography } from '@mui/material';
import { ComponentProps } from 'react';

type LearningBudgetTransactionsDigestProps = {
  isLoading?: boolean;
  variant: 'company' | 'team' | 'individual';
  transactionsDigest?: ApiLearningBudgetTransactionsDigestResponse['data'];
} & ComponentProps<'div'>;

export function LearningBudgetTransactionsDigest({
  variant,
  transactionsDigest,
  isLoading,
  ...props
}: LearningBudgetTransactionsDigestProps) {
  const remainingBudgetPercentage =
    (transactionsDigest?.remainingLearningBudget ?? 0) /
    (transactionsDigest?.totalLearningBudget ?? 0);

  return (
    <div {...props}>
      <header>
        <Typography variant="h3">Learning Budget Transactions</Typography>
        <Typography>
          Track and manage learning budget allocations{' '}
          {variant === 'company'
            ? 'in your company'
            : variant === 'team'
              ? 'per team'
              : variant === 'individual'
                ? 'per employee'
                : ''}
        </Typography>
      </header>

      <dl
        className={`flex gap-6 flex-wrap mt-6 ${isLoading ? 'text-disabled' : ''}`}
      >
        <div className="flex flex-col gap-1 min-w-52">
          <Typography
            color="inherit"
            variant="body2"
            component={'dt'}
            className="font-semibold"
          >
            Total Budget
          </Typography>

          <Typography
            color="text.secondary"
            component={'dd'}
            className="whitespace-break-spaces break-all"
          >
            {isLoading ? (
              <Skeleton />
            ) : (
              formatCurrencyValue(
                transactionsDigest?.totalLearningBudget ?? 0,
                'eur',
              )
            )}
          </Typography>
        </div>

        <div className="flex flex-col gap-1 min-w-52">
          <Typography
            color="inherit"
            variant="body2"
            component={'dt'}
            className="font-semibold"
          >
            Budget Used
          </Typography>
          <Typography
            color="text.secondary"
            component={'dd'}
            className="whitespace-break-spaces break-all"
          >
            {isLoading ? (
              <Skeleton />
            ) : (
              formatCurrencyValue(
                Math.abs(transactionsDigest?.usedLearningBudget ?? 0),
                'eur',
              )
            )}
          </Typography>
        </div>

        <div className="flex flex-col gap-1 min-w-52">
          <Typography
            color="inherit"
            variant="body2"
            component={'dt'}
            className="font-semibold"
          >
            Remaining Budget
          </Typography>
          <Typography
            color="text.secondary"
            component={'dd'}
            className="whitespace-break-spaces break-all"
          >
            {isLoading ? (
              <Skeleton />
            ) : (
              <>
                {formatCurrencyValue(
                  transactionsDigest?.remainingLearningBudget ?? 0,
                  'eur',
                )}{' '}
                {Number.isFinite(remainingBudgetPercentage) && (
                  <span className="text-secondary">
                    (
                    {Intl.NumberFormat(undefined, {
                      style: 'percent',
                      maximumFractionDigits: 2,
                    }).format(remainingBudgetPercentage)}
                    )
                  </span>
                )}
              </>
            )}
          </Typography>
        </div>

        {variant === 'company' && (
          <div className="flex flex-col gap-1 min-w-52">
            <Typography
              color="inherit"
              variant="body2"
              component={'dt'}
              className="font-semibold"
            >
              Employees Using LB
            </Typography>
            <div className="flex text-secondary gap-2 items-center">
              <PeopleAltOutlined
                color="inherit"
                className={isLoading ? 'animate-pulse' : ''}
              />
              <Typography
                color="inherit"
                component={'dd'}
                className="whitespace-break-spaces break-all"
              >
                {isLoading ? (
                  <Skeleton width={50} />
                ) : (
                  transactionsDigest?.uniqueEmployeeCount ?? 0
                )}
              </Typography>
            </div>
          </div>
        )}
      </dl>
    </div>
  );
}
